import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser } from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [apiKey, setApiKey] = useState(() => localStorage.getItem('serverApiKey') || '');

    const fetchCurrentUser = useCallback(async (key) => {
        if (!key) {
            setCurrentUser(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const user = await getCurrentUser(key);
            setCurrentUser(user);
            setError(null); // Clear any previous errors on success
        } catch (err) {
            console.error('Failed to fetch current user:', err);
            // If API key is invalid or user endpoint fails, treat as legacy admin key
            if (err.status === 401 || err.status === 403) {
                setError('Invalid credentials');
                setCurrentUser(null);
                // Clear the invalid key from storage
                localStorage.removeItem('serverApiKey');
                setApiKey('');
            } else {
                // For other errors (like 404 or if endpoint doesn't exist), 
                // assume it's a legacy admin key - treat as admin with no user object
                setCurrentUser({ role: 'Admin', isLegacy: true });
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (apiKey) {
            fetchCurrentUser(apiKey);
        } else {
            setCurrentUser(null);
            setLoading(false);
        }
    }, [apiKey, fetchCurrentUser]);

    const login = useCallback((newApiKey) => {
        // Clear any previous error when attempting new login
        setError(null);
        localStorage.setItem('serverApiKey', newApiKey);
        setApiKey(newApiKey);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('serverApiKey');
        setApiKey('');
        setCurrentUser(null);
    }, []);

    const refreshUser = useCallback(() => {
        if (apiKey) {
            fetchCurrentUser(apiKey);
        }
    }, [apiKey, fetchCurrentUser]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const isAdmin = currentUser?.role === 'Admin';
    const isDev = currentUser?.role === 'Dev';
    const isPartner = currentUser?.role === 'Partner';

    const value = {
        currentUser,
        loading,
        error,
        apiKey,
        login,
        logout,
        refreshUser,
        clearError,
        isAdmin,
        isDev,
        isPartner,
        isAuthenticated: !!apiKey && !!currentUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

